import styled, { css, keyframes } from 'styled-components';
import { UiColors } from 'assets/styles/old/global';

export const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const Trigger = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border: none;
  background-color: ${UiColors.forestColor};
  color: ${UiColors.peachColor};
  font-size: 14px;
  font: inherit;
  font-weight: 500;
  cursor: pointer;
  transition: filter 0.2s;

  &:hover {
    filter: saturate(3);
  }

  &:focus {
    border: 3px solid var(--dodger-blue);
    outline-color: var(--dodger-blue);
  }

  /* Neither trigger carried a radius, so both stayed square while the buttons beside them rounded.
     Same tokens ui-kit/Button uses; a focus ring replaces the 3px border, which reflowed the label.
     html[...] not :root[...] -- stylis auto-prefixes a selector opening with a pseudo-class. */
  html[data-new-ui='true'] & {
    border-radius: var(--btn-radius);
    font-weight: var(--btn-weight);

    &:hover {
      filter: brightness(1.15);
    }

    &:focus {
      border: none;
    }

    &:focus-visible {
      outline: 2px solid var(--dodger-blue);
      outline-offset: 2px;
    }
  }

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}

  svg {
    flex-shrink: 0;
    fill: currentColor;
  }
`;

export const IconTrigger = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  background-color: transparent;
  color: ${UiColors.forestColor};
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.06);
  }

  &:focus {
    border: 3px solid var(--dodger-blue);
    outline-color: var(--dodger-blue);
  }

  html[data-new-ui='true'] & {
    border-radius: var(--btn-radius);

    &:hover {
      background-color: ${UiColors.cloudColor};
    }

    &:focus {
      border: none;
    }

    &:focus-visible {
      outline: 2px solid var(--dodger-blue);
      outline-offset: 2px;
    }
  }

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}

  svg {
    flex-shrink: 0;
    fill: currentColor;
  }
`;

export const Caret = styled.svg`
  transition: transform 0.15s ease;
  ${({ open }) =>
    open &&
    css`
      transform: rotate(180deg);
    `}
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// Distance from the top of the viewport at which the mobile popover sits.
// Small offset so the panel can use ~full viewport height without crowding
// the status bar / browser chrome.
export const MOBILE_PANEL_TOP_OFFSET = 62;

export const Panel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  width: 320px;
  max-width: calc(100vw - 24px);
  max-height: calc(100vh - 120px);
  padding: 18px 18px 16px;
  background: var(--white);
  border: 1px solid var(--gray20, #e5e7eb);
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(13, 13, 13, 0.16);
  box-sizing: border-box;
  animation: ${fadeIn} 140ms ease-out;
`;

export const ScrollBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
  margin-right: -4px;

  /* Enlarge the BaseSelect clear-all hit target so it's reliably tappable on touch.
     Targets only the indicator that's keyboard-focusable (the clear button),
     not the dropdown caret. */
  .BaseSelect__indicators > [tabindex='0'] {
    padding: 10px;
    cursor: pointer;
    touch-action: manipulation;
  }
`;

export const Title = styled.h2`
  margin: 0 0 14px;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-strong);
`;

export const FieldGroup = styled.div`
  margin: 0 0 14px;
`;

export const FieldLabel = styled.div`
  margin: 0 0 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--dark60, #6b7280);
`;

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ count }) => count}, minmax(0, 1fr));
  gap: 8px;
  margin: 0 0 16px;
`;

export const Card = styled.button.attrs({ type: 'button' })`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 8px;
  background: var(--white);
  border: 1px solid var(--gray20, #e5e7eb);
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease, box-shadow 120ms ease;
  font: inherit;
  color: var(--text-strong);

  &:hover {
    border-color: var(--dark60, #6b7280);
  }

  &:focus-visible {
    outline: none;
    border-color: var(--dark);
    box-shadow: 0 0 0 2px rgba(13, 13, 13, 0.15);
  }

  ${({ selected }) =>
    selected &&
    css`
      border-color: var(--dark);
      background: var(--gray10, #f6f4ee);
      box-shadow: inset 0 0 0 1px var(--dark);
    `}
`;

export const CardLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
`;

export const CardSublabel = styled.span`
  margin-top: 2px;
  font-size: 11px;
  color: var(--dark60, #6b7280);
`;

export const SubmitWrapper = styled.div`
  flex-shrink: 0;
  margin-top: 12px;
  display: flex;
  gap: 8px;

  button {
    flex: 1 1 0;
    min-width: 0;
  }
`;

export const RadioStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 0 16px;
`;

export const RadioOption = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  background: var(--white);
  border: 1px solid var(--gray20, #e5e7eb);
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease, box-shadow 120ms ease;
  font: inherit;
  color: var(--text-strong);
  text-align: left;

  &:hover {
    border-color: var(--dark60, #6b7280);
  }

  &:focus-visible {
    outline: none;
    border-color: var(--dark);
    box-shadow: 0 0 0 2px rgba(13, 13, 13, 0.15);
  }

  ${({ selected }) =>
    selected &&
    css`
      border-color: var(--dark);
      background: var(--gray10, #f6f4ee);
      box-shadow: inset 0 0 0 1px var(--dark);
    `}
`;

export const RadioDot = styled.span`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-top: 2px;
  border: 1.5px solid var(--gray60, #9ca3af);
  border-radius: 50%;
  background: var(--white);
  position: relative;

  ${({ selected }) =>
    selected &&
    css`
      border-color: var(--dark);

      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--dark);
      }
    `}
`;

export const RadioContent = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const RadioLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
`;

export const RadioSublabel = styled.span`
  font-size: 11px;
  color: var(--dark60, #6b7280);
  line-height: 1.3;
`;
